import {Box, SxProps } from "@mui/system";
import {Button, Typography, CircularProgress} from "@mui/material";
import LoginTextField from "../LoginTextField/LoginTextField";
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import WarningTwoToneIcon from '@mui/icons-material/WarningTwoTone';
import LockClockTwoToneIcon from '@mui/icons-material/LockClockTwoTone';
import IconButton from '@mui/material/IconButton';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import React, {useState, FormEvent, useCallback} from "react";
import type { passwordError } from "../../views/Login";

type PasswordBoxProps = {
    value: string
    username: string
    setPassword: Function
    onCancel: Function
    handleLogin: (ev: FormEvent) => Promise<void>
    error?: passwordError,
    loading: boolean
}

const formSx: SxProps = {
    display: 'flex',
    flexDirection: 'column',
    height: '100%'
};

const boxSx: SxProps = {
    display: 'flex',
    flex: 1,
    justifyContent: 'flex-end',
    flexDirection: 'column'
};

const moreInfoBoxSx: SxProps = {
    display: 'flex',
    padding: 1,
    marginBottom: 2,
    backgroundColor: 'rgba(0, 20, 0, 0.1)',
    borderRadius: 1
};

const accountLockedBoxSx: SxProps = {
    ...moreInfoBoxSx,
    backgroundColor: 'warning.main',
};

const attemptText = (attempts: number) => attempts > 1 ? 'attempts' : 'attempt';

const lockedOutTimeText = (lockedOutTime: number) =>  lockedOutTime < 60 ?
    `${Math.ceil(lockedOutTime)} seconds`:
    `${Math.ceil(lockedOutTime/60)} minutes`;

const PasswordBox = ({
    value,
    username,
    setPassword,
    onCancel,
    handleLogin,
    error,
    loading
}: PasswordBoxProps) => {
    const passwordHelperText = (): JSX.Element | string => <>
        {error?.code ? 'Please enter the correct password. Forgotten your password' : 'Lost your password'}? Go <a
            href={`service/forgot-password?username=${username}`}
        >here</a> to reset it.
    </>;

    const [showPassword, setShowPassword] = useState(false);

    const handleClickShowPassword = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        setShowPassword((show) => !show);
    }, [setShowPassword]);

    const showWarning = error?.detail.remaining_attempts && error?.detail.remaining_attempts <= 3;
    const showAccountLockedWarning = !showWarning && error?.code === 'account-locked';

    return (
        <Box component="form" onSubmit={handleLogin} sx={formSx}>
            <Typography variant="h2" gutterBottom>
                Hello there!
            </Typography>
            <Typography variant="body1" gutterBottom>
                Nice to see you <strong>{username}</strong>. Please enter your password to&nbsp;continue.
            </Typography>
            <LoginTextField
                id="password"
                type={showPassword ? 'text' : 'password'}
                label="Password:"
                helperText={passwordHelperText()}
                value={value}
                error={error?.code != ''}
                required={true}
                onChangeHandler={useCallback((ev: Event, value: string) => setPassword(value), [setPassword])}
                InputProps={{
                    endAdornment: <IconButton
                        component="button"
                        aria-label="toggle password visibility"
                        title={showPassword ? 'Hide password' : 'Show password'}
                        onClick={handleClickShowPassword}
                        edge="end"
                    >
                        {showPassword ? <VisibilityOff/> : <Visibility/>}
                    </IconButton>
                }}
            />


            <Box sx={boxSx}>
                { showWarning &&
                    <Box sx={moreInfoBoxSx}>
                        <WarningTwoToneIcon> </WarningTwoToneIcon>
                        <Typography variant="body1" sx={{marginLeft: 1}}>
                            You
                            have <strong>{error?.detail.remaining_attempts} { error?.detail.remaining_attempts && attemptText(error?.detail.remaining_attempts)} </strong>left
                            until your account gets temporarily
                            locked.
                        </Typography>
                    </Box>
                }

                { showAccountLockedWarning &&
                    <Box sx={accountLockedBoxSx}>
                        <LockClockTwoToneIcon sx={{color: 'warning.contrastText'}}> </LockClockTwoToneIcon>
                        <Typography variant="body1" sx={{marginLeft: 1}}>
                            Your account is temporarily locked. Please
                            wait <strong>{error?.detail.remaining_lockout_seconds && lockedOutTimeText(error?.detail.remaining_lockout_seconds)}</strong> before trying
                            again.
                        </Typography>
                    </Box>
                }

                <Box sx={{ display: 'flex', gap: 1}}>
                    <Button
                        id="cancel-button"
                        variant="outlined"
                        color="primary"
                        sx={{ flex: 1 }}
                        onClick={() => onCancel()}
                    >
                        Cancel
                    </Button>
                    <Button
                        id="submit-button"
                        variant="contained"
                        color="success"
                        sx={{ flex: 1 }}
                        disabled={loading || value === ''}
                        type={"submit"}
                        endIcon={
                            loading ?
                                <CircularProgress size={24} color={'inherit'}/> :
                                <NavigateNextIcon sx={{marginBottom: '-4px'}}/>
                        }
                    >
                        Continue
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};

export default PasswordBox;
